import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { formatSessionDate } from '@/lib/datetime';
import { fetchCadeVistas } from '@/services/cade';
import type { CadeVistaList, CadeVistaRequester, CadeVistaRow } from '@/services/types';

/**
 * Pedidos de vista: how long the case stayed away, and who asked.
 *
 * A delay is a gap between two archived session dates, so a case with no later archived
 * decision is OPEN, never a delay. Requesters are listed by NAME, never ranked, with the count
 * the ata recorded: a vista is a right every conselheiro holds. Every case behind every figure
 * is in the table below, each linking to its page.
 */
export function CadeVistaPage() {
  const { t } = useTranslation(['cade']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'vistas'],
    queryFn: fetchCadeVistas,
  });

  return (
    <div className="space-y-4 p-6" data-vistas>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:vista.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:vista.subtitle')}</p>
      </header>
      {data ? <AsOfStrip asOf={data.as_of} /> : null}
      {isPending ? <SkeletonLoader rows={6} /> : null}
      {error ? <ErrorMessage message={error.message} onRetry={() => void refetch()} /> : null}
      {data ? (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-foreground">{t('cade:vista.delayTitle')}</h2>
            <ExportButton path="/api/v1/cade/export/vistas" />
          </div>
          <Answer answer={data.vistas} help={t('cade:vista.help')} scope="record">
            {(list: CadeVistaList) => <VistaBody list={list} />}
          </Answer>
        </section>
      ) : null}
    </div>
  );
}

function VistaBody({ list }: { list: CadeVistaList }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const delay = list.delay;
  return (
    <div className="space-y-4">
      <p className="text-sm text-foreground" data-vista-summary>
        {t('cade:vista.summary', {
          items: number.format(list.items),
          cases: number.format(list.cases),
          returned: number.format(list.returned),
          open: number.format(list.open_items),
        })}
      </p>

      {delay ? (
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-vista-delay>
          {(
            [
              ['p25', delay.p25],
              ['median', delay.median],
              ['p75', delay.p75],
              ['p90', delay.p90],
              ['maximum', delay.maximum],
            ] as const
          ).map(([rung, days]) => (
            <div key={rung} className="rounded-md border p-2">
              <dt className="text-xs text-muted-foreground">{t(`cade:vista.rung.${rung}`)}</dt>
              <dd className="text-sm font-medium tabular-nums text-foreground">
                {t('cade:vista.days', { count: Math.round(days) })}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-xs text-muted-foreground" data-vista-no-ladder>
          {t('cade:vista.noLadder', { count: list.returned })}
        </p>
      )}

      <RequesterTable list={list} />

      <div className="max-h-[70vh] overflow-auto rounded-lg border" data-vista-rows>
        <table className="w-full text-sm">
          <caption className="sr-only">{t('cade:vista.casesTitle')}</caption>
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-foreground">
            <tr>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.case')}
              </th>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.session')}
              </th>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.requester')}
              </th>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.next')}
              </th>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.delay')}
              </th>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.outcome')}
              </th>
            </tr>
          </thead>
          <tbody>
            {list.rows.map((row, index) => (
              <VistaTableRow key={`${row.case_key}-${row.item_number}-${index}`} row={row} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RequesterTable({ list }: { list: CadeVistaList }) {
  const { t } = useTranslation(['cade']);
  if (list.requesters.length === 0) return null;
  return (
    <section className="space-y-1" data-vista-requesters>
      <h3 className="text-xs font-semibold text-foreground">{t('cade:vista.requestersTitle')}</h3>
      <p className="text-xs text-muted-foreground">
        {t('cade:vista.requestersNote', {
          unnamed: list.unnamed,
          floor: list.min_requester_returns,
        })}
      </p>
      <div className="overflow-auto rounded-lg border">
        <table className="w-full text-sm">
          <caption className="sr-only">{t('cade:vista.requestersTitle')}</caption>
          <thead className="bg-muted text-left text-xs text-foreground">
            <tr>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.requester')}
              </th>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.requests')}
              </th>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.returned')}
              </th>
              <th scope="col" className="px-3 py-2">
                {t('cade:vista.col.median')}
              </th>
            </tr>
          </thead>
          <tbody>
            {list.requesters.map((requester) => (
              <RequesterLine key={requester.name} requester={requester} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RequesterLine({ requester }: { requester: CadeVistaRequester }) {
  const { t } = useTranslation(['cade']);
  return (
    <tr className="border-t" data-requester={requester.name}>
      <td className="px-3 py-1.5 text-foreground">
        {requester.name}
        {requester.resolved ? null : (
          <span className="ml-1 text-xs text-muted-foreground">{t('cade:vista.asMinuted')}</span>
        )}
      </td>
      <td className="px-3 py-1.5 tabular-nums text-foreground">{requester.requests}</td>
      <td className="px-3 py-1.5 tabular-nums text-foreground">{requester.returned}</td>
      <td className="px-3 py-1.5 tabular-nums text-foreground">
        {requester.median_days === null
          ? t('cade:vista.withheld')
          : t('cade:vista.days', { count: Math.round(requester.median_days) })}
      </td>
    </tr>
  );
}

function VistaTableRow({ row }: { row: CadeVistaRow }) {
  const { t, i18n } = useTranslation(['cade']);
  const date = (iso: string | null) => formatSessionDate(iso, null, i18n.language);
  return (
    <tr className="border-t align-top" data-vista-row>
      <td className="px-3 py-1.5">
        <Link to={`/cade/c/${row.case_key}`} className="text-primary underline underline-offset-4">
          {row.case_number}
        </Link>
        {row.item_number ? (
          <span className="ml-1 text-xs text-muted-foreground">
            {t('cade:vista.item', { number: row.item_number })}
          </span>
        ) : null}
      </td>
      <td className="px-3 py-1.5 tabular-nums text-foreground">{date(row.session_date) ?? '—'}</td>
      <td className="px-3 py-1.5 text-foreground">
        {row.requester ?? <span className="text-muted-foreground">{t('cade:vista.noName')}</span>}
      </td>
      <td className="px-3 py-1.5 tabular-nums text-foreground">{date(row.next_date) ?? '—'}</td>
      <td className="px-3 py-1.5 tabular-nums text-foreground">
        {row.days === null ? (
          <span data-vista-open>{t('cade:vista.open')}</span>
        ) : (
          t('cade:vista.days', { count: row.days })
        )}
      </td>
      <td className="px-3 py-1.5 text-foreground">
        {row.next_outcomes
          .map((code) => t(`cade:overview.outcomes.${code}`, { defaultValue: code }))
          .join(', ') || '—'}
      </td>
    </tr>
  );
}
